import React, { useState, useEffect } from "react";
import { LinkIcon } from "@heroicons/react/24/solid";
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
const BlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [viewingBlog, setViewingBlog] = useState(null);
  const [likeCounts, setLikeCounts] = useState({});
  const [likesStatus, setLikesStatus] = useState({});
  const [comments, setComments] = useState({});
  const [commentInput, setCommentInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [inputValue, setInputValue] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const handleTagChange = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/tags`
        );
        setAvailableTags(res.data);
      } catch (err) {
        console.error("Failed to fetch tags:", err.message);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const username = sessionStorage.getItem("username");
        const email = sessionStorage.getItem("email");

        if (!username || !email) {
          console.error(
            "Username or email is not available in sessionStorage."
          );
          return;
        }

        const isTagFiltering = selectedTags.length > 0;
        const endpoint = isTagFiltering
          ? `${process.env.REACT_APP_API_URL}/api/posts/by-tags`
          : `${process.env.REACT_APP_API_URL}/api/posts`;

        const params = {
          username,
          email,
          page: currentPage,
          limit: postsPerPage,
        };

        if (isTagFiltering) {
          params.tags = selectedTags; // Tags filtering
        } else {
          params.search = activeSearchTerm; // Regular search
        }

        const response = await axios.get(endpoint, { params });

        if (response && response.data) {
          const fetchedPosts = response.data.posts;

          const initialLikeCounts = {};
          const initialLikesStatus = {};
          fetchedPosts.forEach((post) => {
            initialLikeCounts[post._id] = post.totalReactions || 0;
            initialLikesStatus[post._id] = post.hasUserReacted || false;
          });

          setPosts(fetchedPosts);
          setFilteredPosts(fetchedPosts);
          setLikeCounts(initialLikeCounts);
          setLikesStatus(initialLikesStatus);
          setTotalPages(response.data.meta.totalPages || 1);
        }
      } catch (err) {
        console.error("Error fetching posts:", err.response || err.message);
      }
    };

    fetchPosts();
  }, [currentPage, postsPerPage, activeSearchTerm, selectedTags]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setActiveSearchTerm(inputValue.trim());
      console.log("Searching for:", inputValue.trim()); // Replace with your actual search logic
      setCurrentPage(1);
    }
  };

  const handleFindBlogs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/posts`,
        {
          params: {
            tags: selectedTags,
          },
          paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
          },
        }
      );
      setFilteredPosts(response.data.posts);
    } catch (error) {
      console.error("Failed to fetch filtered blogs:", error.message);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value); // Only updates the input field
  };

  const clearSearch = () => {
    setInputValue("");
    setActiveSearchTerm("");
    console.log("Cleared search"); // Replace with logic to reset results
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    const fetchComments = async (blogId) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/comments/${blogId}`
        );
        setComments((prev) => ({
          ...prev,
          [blogId]: response.data,
        }));
      } catch (error) {
        console.error("Error fetching comments:", error);
      }
    };

    if (viewingBlog) {
      fetchComments(viewingBlog._id);
    }
  }, [viewingBlog]);

  // Handle search
  useEffect(() => {
    const searchPosts = () => {
      if (!activeSearchTerm.trim()) {
        setFilteredPosts(posts);
        return;
      }

      const query = activeSearchTerm.toLowerCase();
      const filtered = posts.filter(
        (post) =>
          post.title?.toLowerCase().includes(query) ||
          post.content?.toLowerCase().includes(query) ||
          post.username?.toLowerCase().includes(query)
      );
      setFilteredPosts(filtered);
    };

    searchPosts();
  }, [activeSearchTerm, posts]);

  const getPreviewContent = (content, maxLength = 150) => {
    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  const handleLike = async (postId) => {
    try {
      const username = sessionStorage.getItem("username");
      const email = sessionStorage.getItem("email");

      if (!username || !email) {
        console.error("Username or email is not available in sessionStorage.");
        return;
      }

      const currentLiked = likesStatus[postId] || false;
      const newLikedStatus = !currentLiked;

      await axios.post(`${process.env.REACT_APP_API_URL}/api/reactions`, {
        blogId: postId,
        username,
        email,
        reacted: newLikedStatus ? 1 : 0,
      });

      setLikesStatus((prev) => ({
        ...prev,
        [postId]: newLikedStatus,
      }));

      setLikeCounts((prev) => ({
        ...prev,
        [postId]: newLikedStatus
          ? (prev[postId] || 0) + 1
          : Math.max(0, (prev[postId] || 0) - 1),
      }));
    } catch (error) {
      console.error("Error handling like:", error);
    }
  };

  const handleReadMore = (post) => {
    setViewingBlog(post);
    setCommentInput("");
  };

  const handleClose = () => {
    setViewingBlog(null);
  };

  const handleCommentfetch = async (e) => {
    e.preventDefault();
    const user = {
      username: sessionStorage.getItem("username"),
      email: sessionStorage.getItem("email"),
    };

    if (!user.username || !user.email) {
      alert("You must be logged in to comment!");
      return;
    }

    const newComment = {
      text: commentInput,
      blogId: viewingBlog._id,
      username: user.username,
      email: user.email,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/comments`,
        newComment
      );

      if (response.status === 200) {
        setComments((prevComments) => ({
          ...prevComments,
          [viewingBlog._id]: [
            ...(prevComments[viewingBlog._id] || []),
            newComment,
          ],
        }));
        setCommentInput("");
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };

  return (
    <div>
      <div className="min-h-screen p-6 bg-slate-900">
        {/* Search Bar */}
        <div className=" mb-2">
          <form
            onSubmit={handleSearchSubmit}
            className="relative max-w-xl mx-auto"
          >
            <div className="flex">
              <div className="relative flex-grow">
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  placeholder="Search posts by title, content, or author..."
                  className="w-full px-4 py-2 pl-10 pr-8 rounded-l-lg border bg-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {inputValue && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg
                      className="h-5 w-5 text-gray-400 hover:text-gray-600"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="px-6 py-2 bg-green-500 text-white rounded-r-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Search
              </button>
            </div>
          </form>
        </div>
        
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-100 mb-3">
            Filter by Tags
          </h2>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => (
              <label
                key={tag._id}
                className={`px-2 py-1 rounded text-xs font-medium border transition duration-200 cursor-pointer
          ${
            selectedTags.includes(tag.name)
              ? "bg-violet-600 text-white border-violet-600 shadow-sm"
              : "bg-gray-800 text-gray-300 border-gray-600 hover:border-violet-500 hover:text-white"
          }`}
              >
                <input
                  type="checkbox"
                  value={tag.name}
                  checked={selectedTags.includes(tag.name)}
                  onChange={() => handleTagChange(tag.name)}
                  className="hidden"
                />
                {tag.name}
              </label>
            ))}
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-center sm:justify-end items-center sm:items-center mt-4 space-x-2">
          {/* First Page Button */}
          <span
            onClick={() => handlePageChange(1)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === 1}
          >
            <ChevronDoubleLeftIcon className="w-6 h-6" />
          </span>

          {/* Previous Button */}
          <span
            onClick={() => handlePageChange(currentPage - 1)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === 1}
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </span>

          {/* Current Page Display */}
          <span className="text-sm sm:text-sm text-white font-medium">
            Page {currentPage} of {totalPages}
          </span>

          {/* Next Button */}
          <span
            onClick={() => handlePageChange(currentPage + 1)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === totalPages}
          >
            <ChevronRightIcon className="w-6 h-6" />
          </span>

          {/* Last Page Button */}
          <span
            onClick={() => handlePageChange(totalPages)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === totalPages}
          >
            <ChevronDoubleRightIcon className="w-6 h-6" />
          </span>
        </div>

        <h1 className="text-lg mt-8 sm:text-lg md:text-2xl lg:text-2xl text-green-500 font-bold tracking-wide text-center md:text-left mb-5">
          {activeSearchTerm
            ? `Search Results (${filteredPosts.length})`
            : "Latest Blog Posts:"}
        </h1>

        <div className="space-y-6 mt-4">
          {filteredPosts.length === 0 ? (
            <div className="flex flex-col h-screen items-center justify-center mt-8 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-24 w-24 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12" y2="16"></line>
              </svg>
              <p className="text-gray-500 text-lg mt-4">
                {activeSearchTerm
                  ? "No posts found matching your search"
                  : "No posts available"}
              </p>
            </div>
          ) : (
            filteredPosts.map((post) => (
              <div
                key={post._id}
                className="bg-slate-300 rounded-lg shadow-md hover:shadow-lg p-6 space-y-4"
              >
                <h2 className="text-2xl font-semibold">{post.title}</h2>
                <p className="text-sm text-blue-500 font-bold">
                  By {post.username}
                </p>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  className="text-black font-bold "
                >
                  {getPreviewContent(post.heading)}
                </ReactMarkdown>
                <div className="flex justify-between items-center pt-4">
                  <button
                    onClick={() => handleReadMore(post)}
                    className="px-3 py-2 bg-green-500 text-white text-md rounded-md hover:bg-green-600"
                  >
                    Read More
                  </button>

                  <div className="flex gap-4">
                    <button
                      className="ml-2 h-8 w-8 animate-bounce bg-violet-500 text-white rounded-full hover:bg-violet-600 transition ease-in-out duration-300 flex items-center justify-center"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/genblog/${post._id}`
                        );
                        alert("Link copied to clipboard!");
                      }}
                    >
                      <LinkIcon className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleLike(post._id)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md"
                    >
                      <svg
                        className={`w-5 h-5 ${
                          likesStatus[post._id]
                            ? "fill-red-500 stroke-red-500"
                            : "stroke-current"
                        }`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        strokeWidth="2"
                      >
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                      </svg>
                      <span>{likeCounts[post._id] || 0}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-center sm:justify-end items-center sm:items-center mt-4 space-x-2">
          {/* First Page Button */}
          <span
            onClick={() => handlePageChange(1)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === 1}
          >
            <ChevronDoubleLeftIcon className="w-6 h-6" />
          </span>

          {/* Previous Button */}
          <span
            onClick={() => handlePageChange(currentPage - 1)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === 1}
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </span>

          {/* Current Page Display */}
          <span className="text-sm sm:text-sm text-white font-medium">
            Page {currentPage} of {totalPages}
          </span>

          {/* Next Button */}
          <span
            onClick={() => handlePageChange(currentPage + 1)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === totalPages}
          >
            <ChevronRightIcon className="w-6 h-6" />
          </span>

          {/* Last Page Button */}
          <span
            onClick={() => handlePageChange(totalPages)}
            className={`cursor-pointer text-xl sm:text-2xl p-2 rounded-full transition-colors duration-200 ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
            }`}
            role="button"
            aria-disabled={currentPage === totalPages}
          >
            <ChevronDoubleRightIcon className="w-6 h-6" />
          </span>
        </div>
      </div>

      {viewingBlog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-40">
          <div className="relative bg-white p-8 rounded-lg shadow-2xl w-full max-w-3xl max-h-[75vh] overflow-y-auto">
            <button
              className="absolute top-4 right-3 p-1 animate-bounce rounded-md bg-red-500 hover:bg-red-600 text-gray-500 hover:text-gray-800 focus:outline-none"
              onClick={handleClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <h2 className="text-3xl font-extrabold mb-6 text-gray-900 text-center border-b pb-4">
              {viewingBlog.heading}
            </h2>
            {/* Tags Section */}
            {viewingBlog?.tags?.length > 0 && (
              <div className="mt-2 mb-5 flex flex-wrap gap-2 justify-center md:justify-start">
                {viewingBlog.tags.map((tags, index) => (
                  <span
                    key={index}
                    className="bg-gray-200 text-gray-800 text-sm font-semibold px-3 py-1 rounded-full"
                  >
                    #{tags}
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-col items-center space-y-2">
              <p className="text-sm font-mono text-green-600 font-medium">
                <span className="font-bold text-green-700">Created: </span>
                {new Date(viewingBlog.createdAt).toLocaleString()}
              </p>

              <p className="text-sm font-mono text-blue-600 font-medium">
                <span className="font-bold font-mono text-blue-700">
                  Posted by:{" "}
                </span>
                {viewingBlog.username}
              </p>

              <p className="text-sm font-medium flex items-center space-x-2">
                <span className="text-red-500">Reactions: ❤️</span>
                <span className="text-red-500">
                  {viewingBlog.totalReactions}{" "}
                </span>
              </p>
            </div>

            <div className="prose max-w-none text-gray-900 leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {viewingBlog.content}
              </ReactMarkdown>
            </div>

            <div className="mt-4">
              <h4 className="text-lg font-semibold">Comments:</h4>
              <ul>
                {(comments[viewingBlog._id] || []).map((comment, index) => (
                  <li key={index} className="mt-2 p-2 border-b border-gray-300">
                    <p className="font-medium">{comment.username}</p>
                    <p className="text-sm">{comment.text}</p>
                  </li>
                ))}
              </ul>

              <form onSubmit={handleCommentfetch} className="mt-4">
                <textarea
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full p-2 border rounded-md"
                />
                <button
                  type="submit"
                  className="mt-2 px-4 py-2 bg-black text-white rounded-md"
                >
                  Post Comment
                </button>
              </form>
            </div>

            <div className="flex justify-center mt-8">
              <button
                className="px-3 py-2 bg-red-600 text-white  rounded-lg shadow-md hover:bg-red-700 transition ease-in-out duration-300"
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPosts;
